/**
 * reminderStatus.js — Single source of truth untuk status reminder/event.
 *
 * Aturan (149-cq PROMPT 16-R2A):
 *   • 'cancelled' — event dibatalkan (status='cancelled')
 *   • 'done'      — event sudah selesai (completed_at != null)
 *   • 'overdue'   — melewati tanggal/jam target (Asia/Jakarta) dan belum selesai
 *   • 'today'     — pada hari target (WIB) dan belum selesai
 *   • 'upcoming'  — sebelum tanggal target
 *
 * Dipakai konsisten oleh: InfoAgendaPage feed, Dashboard, kalender, badge, filter, dan
 * (via endpoint filter) ikut menjaga label yang sama di sisi backend.
 */

const JKT_TZ = "Asia/Jakarta";

// Ambil tanggal "YYYY-MM-DD" dari epoch di zona Asia/Jakarta (kompatibel semua timezone user).
function _jktISODate(epoch = Date.now()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: JKT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(epoch));
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
}

/**
 * Hitung status event.
 * @param {object} event  — { event_date: "YYYY-MM-DD", has_time?, event_time?, status?, completed_at? }
 * @param {number} [nowEpoch] — override untuk unit test.
 * @returns {'cancelled'|'done'|'overdue'|'today'|'upcoming'}
 */
export function computeReminderStatus(event, nowEpoch = Date.now()) {
  if (!event) return "upcoming";
  if (event.status === "cancelled") return "cancelled";
  if (event.completed_at != null) return "done";
  const raw = event.event_date;
  if (!raw) return "upcoming";
  const targetDate = String(raw).slice(0, 10);
  const todayJKT = _jktISODate(nowEpoch);
  if (targetDate < todayJKT) return "overdue";
  if (targetDate > todayJKT) return "upcoming";
  // Same-day WIB: kalau ada jam eksplisit dan sudah lewat, treat overdue.
  if (event.has_time && event.event_time) {
    const nowHM = new Intl.DateTimeFormat("en-GB", {
      timeZone: JKT_TZ, hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(new Date(nowEpoch));
    // event_time format "HH:MM" atau "HH:MM:SS"
    const evHM = String(event.event_time).slice(0, 5);
    if (evHM && nowHM > evHM) return "overdue";
  }
  return "today";
}

/**
 * Label bahasa Indonesia untuk status.
 */
export const REMINDER_STATUS_LABEL = {
  cancelled: "Dibatalkan",
  done: "Selesai",
  overdue: "Terlambat",
  today: "Hari Ini",
  upcoming: "Akan Datang",
};

/**
 * Kelas warna badge Tailwind untuk status.
 */
export const REMINDER_STATUS_CLASS = {
  cancelled: "bg-slate-100 text-slate-500 border-slate-300",
  done:      "bg-emerald-50 text-emerald-700 border-emerald-300",
  overdue:   "bg-rose-100 text-rose-800 border-rose-400 font-bold",
  today:     "bg-sky-100 text-sky-800 border-sky-400",
  upcoming:  "bg-slate-50 text-slate-600 border-slate-300",
};
